<template>
  <div class="relative h-full w-full overflow-hidden">
    <strapi-image
      class="brightness-[0.6] mask-[linear-gradient(to_bottom,#fff_40%,transparent_90%)] object-cover w-full"
      :image="item.backgroundImage"
      :lazy="!isActive"
      as-background
      :alt-fallback="item.title"
    />
    <div
      class="h-full md:h-[calc(100%-100px)] relative max-w-screen-xl mx-auto py-4 md:py-0 px-4 flex flex-col justify-start md:justify-center text-center md:text-left"
    >
      <h2 class="uppercase">{{ item.headline }}</h2>
      <h1 class="uppercase">{{ item.title }}</h1>
      <p class="w-full xl:w-3/4 text-xl m-0">{{ item.text }}</p>

      <div
        v-if="item.buttons.length > 0"
        class="w-full sm:w-2/3 md:w-full lg:w-auto mt-4 md:mt-8 md:mx-0 flex flex-wrap gap-4 items-center md:items-start justify-center md:justify-start sm:mx-auto"
      >
        <common-button
          v-for="btn in item.buttons"
          :key="btn.id"
          v-bind="linkAttrs(btn)"
          :variant="variantFor(btn.type)"
          :outline="outlineFor(btn.type)"
          class="inline-flex items-center justify-center whitespace-nowrap max-w-full min-w-0"
        >
          <fa-icon
            v-if="isConsoleIcon(btn.text)"
            :icon="btn.text === 'PlayStation' ? faPlaystation : faXbox"
            class="size-5"
            aria-hidden="true"
          />
          <span v-else class="truncate min-w-0">
            {{ btn.text }}
          </span>
        </common-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { faPlaystation, faXbox } from '@fortawesome/free-brands-svg-icons';
import type {
  StrapiBlockbusterItemComponent,
  StrapiButton,
} from '~/apollo/types/types';
import { useNuxtRoute } from '~/utils/useNuxtRoute';

defineProps<{
  item: StrapiBlockbusterItemComponent;
  isActive: boolean;
}>();

const { isNuxtRoute } = useNuxtRoute();

const isConsoleIcon = (text: string) => ['PlayStation', 'Xbox'].includes(text);

const variantFor = (type: string): 'primary' | 'secondary' =>
  type === 'dark' ? 'secondary' : 'primary';
const outlineFor = (type: string) =>
  type === 'dark' || type === 'outline-primary';

const isSafeExternalUrl = (url: string | null | undefined): url is string =>
  typeof url === 'string' && /^https:\/\//i.test(url);

const linkAttrs = (btn: StrapiButton) => {
  const target = btn.newTab ? '_blank' : '_self';
  const label = isConsoleIcon(btn.text) ? { 'aria-label': btn.text } : {};
  if (isNuxtRoute(btn.link)) {
    return { to: btn.link, target, ...label };
  }
  if (!isSafeExternalUrl(btn.link)) {
    return { ...label };
  }
  return {
    href: btn.link,
    target,
    ...label,
    ...(btn.newTab ? { rel: 'noopener noreferrer' } : {}),
  };
};
</script>
